<script lang="ts" module>
	const cache: Record<string, Promise<Policy>> = {};
</script>

<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { FieldPropertiesSchema, LegalFieldPropertiesSchema, PolicyRefSchema, type Field } from "#gen/forms/v1/field_pb.js";
	import type { Policy } from "#gen/legal/v1/legal_pb.js";
	import type { LegalClient } from "#lib/api/legal.js";
	import P from "#lib/components/P.svelte";
	import HtmlViewer from "#lib/components/content-editors/HtmlViewer.svelte";
	import PolicySelector from "#lib/components/flow/editor/builder/properties/reference/PolicySelector.svelte";
	import FieldHeader from "#lib/components/page/components/form/FieldHeader.svelte";
	import type { FieldProps, TypeIcon } from "#lib/components/page/components/form/FieldProps.js";
	import { Logger } from "#lib/logging.js";

	interface Props extends FieldProps {
		typeIcon?: TypeIcon | undefined;
		field: Field;
		active?: boolean;
		editable?: boolean;
		onInput?: (field: Field) => void;
		onChange?: (field: Field) => void;
	}

	const legalClient: LegalClient = getContext("legalClient");

	let { typeIcon, field, editable, readOnly, onInput, onChange, ...restProps }: Props = $props();
	let selectedPolicy: Policy | undefined = $state();

	const inputDisabled = $derived(editable || readOnly || !field.active);

	$effect(() => {
		if (field.properties?.properties.case === "legal" && field.properties?.properties?.value?.policyRef?.id) {
			loadPolicy(field.properties?.properties.value?.policyRef?.id, field.properties?.properties.value?.policyRef?.version);
		}
	});

	export async function validate(): Promise<boolean> {
		return true;
	}

	async function loadPolicy(id: string, version: string) {
		try {
			selectedPolicy = await (cache[id] = cache[id] || legalClient.getPolicy(id, version));
		} catch (err) {
			Logger.error("Failed to load policy:", { err });
		}
	}

	function handleSetPolicy(policy: Policy) {
		if (policy.id === selectedPolicy?.id && policy.version === selectedPolicy?.version) {
			return;
		}
		if (!field.properties) {
			field.properties = create(FieldPropertiesSchema);
		}
		selectedPolicy = policy;
		field.properties.properties = {
			case: "legal",
			value: create(LegalFieldPropertiesSchema, {
				policyRef: create(PolicyRefSchema, { id: policy.id, version: policy.version }),
			}),
		};
		onInput?.(field);
		onChange?.(field);
	}
</script>

<div {...restProps}>
	<div class="flex items-center justify-between gap-4">
		<div class="grow">
			{#if editable}
				<FieldHeader {typeIcon} {field} on:input={e => onInput?.(e.detail)} on:change={e => onChange?.(e.detail)} />
			{/if}
			{#if editable && !field.immutable}
				<PolicySelector {selectedPolicy} onselect={handleSetPolicy}></PolicySelector>
			{:else if selectedPolicy?.content?.content.case === "text"}
				<div class={twMerge("form-terms-text text-xs text-gray-900", inputDisabled && "pointer-events-none cursor-pointer opacity-100")}>
					<HtmlViewer content={selectedPolicy?.content?.content.value ?? ""}></HtmlViewer>
				</div>
			{:else if selectedPolicy?.content?.content.case === "url"}
				<a href={selectedPolicy?.content?.content.value ?? ""} target="_blank" rel="external noopener" class={twMerge("form-terms-link text-blue-600 underline", inputDisabled && "pointer-events-none cursor-pointer opacity-100")}>
					{field.title}
				</a>
			{/if}
			{#if field.description && !editable}
				<P>{field.description}</P>
			{/if}
		</div>
	</div>
</div>
